import {Component, computed, effect, inject, input} from '@angular/core';
import {MatTooltip} from '@angular/material/tooltip';
import {avatarColor, initials, Vote, VOTE_COLORS, VOTE_LABELS} from '../ui-colors';

const SIZES = {xs: 22, sm: 26, md: 32, lg: 56};

/**
 * Round avatar with white initials on a fixed color per user (`seed` = user id).
 * With `vote` it becomes a reviewer avatar: a 3px border in the vote color and "Naam · Stem" as tooltip.
 */
@Component({
    selector: 'app-avatar',
    templateUrl: './avatar.component.html',
    styleUrl: './avatar.component.scss',
    hostDirectives: [MatTooltip],
    host: {
        '[class.reviewer]': '!!vote()',
        '[style.width.px]': 'px()',
        '[style.height.px]': 'px()',
        '[style.font-size.px]': 'fontSize()',
        '[style.background]': 'color() || seedColor()',
        '[style.border-color]': 'vote() ? voteColor() : null',
        '[attr.aria-label]': 'label()',
        'role': 'img'
    }
})
export class AvatarComponent {
    readonly name = input<string>('');
    readonly seed = input<number | string | null>(null);
    readonly color = input<string>();
    readonly size = input<keyof typeof SIZES>('md');
    readonly vote = input<Vote | null>(null);
    /** Defaults to "Naam · Stem" for reviewers; pass an empty string to disable. */
    readonly tooltip = input<string | null>(null);

    readonly letters = computed(() => initials(this.name()));
    readonly seedColor = computed(() => avatarColor(this.seed() ?? this.name()));
    readonly voteColor = computed(() => VOTE_COLORS[this.vote()]);
    readonly px = computed(() => this.vote() && this.size() === 'md' ? 28 : SIZES[this.size()]);
    readonly fontSize = computed(() => Math.max(9, Math.round(this.px() * .36)));
    readonly label = computed(() => this.vote() ? `${this.name()} · ${VOTE_LABELS[this.vote()]}` : this.name());

    private readonly matTooltip = inject(MatTooltip);

    constructor() {
        effect(() => {
            this.matTooltip.message = this.tooltip() ?? (this.vote() ? this.label() : '');
        });
    }
}
